import React from 'react';

import currencyFormatter from 'currency-formatter';
import { Link as RouterLink } from 'react-router-dom';

import BadgeIcon from '@mui/icons-material/Badge';
import DateRangeIcon from '@mui/icons-material/DateRange';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SailingIcon from '@mui/icons-material/Sailing';
import { Accordion, AccordionDetails, AccordionSummary, Button, Divider, Link, Stack, Typography } from '@mui/material';

import { Departure } from './mappers';

const buildAccordionStyle = (departureId: string, leadDepartureId: string | null) => {
  if (departureId !== leadDepartureId) return { border: '1px solid #c8c8c8', borderRadius: '8px' };
  else return { border: '1.5px solid #2c8a3a', borderRadius: '8px', svg: { fill: '#2c8a3a' } };
};

type Props = {
  departures: Array<Departure>;
  leadDepartureId: string | null;
  onHandleDeparture: (departureId: string | null) => void;
};

function DeparturesDetails(props: Props) {
  const { departures, leadDepartureId, onHandleDeparture } = props;

  const handleDeparture = (departureId: string) => {
    if (departureId === leadDepartureId) onHandleDeparture(null);
    else onHandleDeparture(departureId);
  };

  return (
    <Stack spacing={1}>
      {departures.map((departure) => (
        <Accordion key={departure.id} sx={buildAccordionStyle(departure.id, leadDepartureId)}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            id={`${departure.id}-header`}
            aria-controls={`${departure.id}-content`}
            sx={{
              userSelect: 'text',
              '& .MuiAccordionSummary-content': { userSelect: 'text' },
            }}
          >
            <Stack direction="row" width="100%" gap="24" pr={2}>
              <Stack direction="row" flex={1.7} alignItems="flex-end">
                <BadgeIcon sx={{ width: '24px', height: '24px', marginRight: '8px' }} />
                <Typography variant="body1">
                  ID:{' '}
                  <Link component={RouterLink} to={`/cruises/sailings/${departure.id}`}>
                    {departure.id}
                  </Link>
                </Typography>
              </Stack>
              <Divider orientation="vertical" sx={{ borderColor: '#000', margin: '0 16px' }} />
              <Stack direction="row" flex={1} alignItems="flex-end">
                <SailingIcon sx={{ width: '24px', height: '24px', marginRight: '8px' }} />
                <Typography variant="body1">Sailing ID: {departure.externalId}</Typography>
              </Stack>
              <Divider orientation="vertical" sx={{ borderColor: '#000', margin: '0 16px' }} />
              <Stack direction="row" flex={0.7} alignItems="flex-end">
                <DateRangeIcon sx={{ width: '24px', height: '24px', marginRight: '8px' }} />
                <Typography variant="body1">{departure.departureDate}</Typography>
              </Stack>
              <Divider orientation="vertical" sx={{ borderColor: '#000', margin: '0 16px' }} />
              <Stack flex={0.7}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleDeparture(departure.id)}
                  color={departure.id === leadDepartureId ? 'error' : 'success'}
                >
                  {departure.id === leadDepartureId ? 'Remove as Lead' : 'Mark as Lead'}
                </Button>
              </Stack>
            </Stack>
          </AccordionSummary>

          <AccordionDetails>
            <Stack direction="row" width="100%" gap="24">
              {Object.entries(departure.lowestPriceByCabinCategories)?.map(([key, value]) => (
                <Stack key={`${key}-${value.price}`} flex={1} sx={{ textAlign: 'center' }}>
                  <Typography variant="body1">
                    <b>{key}</b> from
                  </Typography>
                  <Typography variant="body1">
                    {currencyFormatter.format(value.price, { code: value.currencyCode, precision: 0 })} / person
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
    </Stack>
  );
}

export default DeparturesDetails;
